import { useState, useEffect } from 'react';
import { useParams, useOutletContext, Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import PageHeader from '../components/PageHeader.jsx';
import { fmtR } from '../lib/money.js';

// The campaigns + segments an ORGANISER linked to an event they SHARED with this
// client — read-only, headline numbers only (server/suiteShares.js). Reached from
// the shared event's row in the nav. Mobile-first: one stacked column of cards.
export default function SharedSuiteEngage() {
  const { suiteId } = useParams();
  const { previewEntityId: entityId } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    setData(null); setError('');
    if (!entityId || !suiteId) return undefined;
    api.mySharedSuiteEngage(entityId, suiteId).then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setError(e.message || 'Could not load this event.'); });
    return () => { alive = false; };
  }, [entityId, suiteId]);

  const pct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : '—');
  const fmt = (n) => (typeof n === 'number' && n >= 0 ? n.toLocaleString() : '—');
  const when = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

  return (
    <div style={{ padding: '0 0 40px' }}>
      <PageHeader kicker={data ? `Shared by ${data.ownerName}` : 'Shared event'} title={data ? data.suiteName : 'Campaigns & segments'} icon="campaigns" />
      <div style={{ maxWidth: 760, padding: '0 14px' }}>
        <p style={hint}>Read-only. These are the organiser’s own campaigns and segments for this event — the numbers, not the audiences or the copy. You can’t edit or send from here.</p>
        {error && <div style={{ ...hint, color: 'var(--danger,#dc2626)', fontWeight: 600 }}>{error}</div>}
        {!data && !error && <div style={hint}>Loading…</div>}
        {data && data.includes.includes('campaigns') && (
          <section style={{ marginTop: 18 }}>
            <h3 style={h3}>Campaigns <span style={count}>{data.campaigns.length}</span></h3>
            {data.campaigns.length === 0 && <div style={hint}>No campaigns are linked to this event yet.</div>}
            {data.campaigns.map((c) => (
              <div key={c.id} style={card}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 14, flex: 1, minWidth: 0 }}>{c.title || '(untitled)'}</span>
                  <span style={pill}>{c.status}</span>
                  <span style={pillMuted}>{c.channel}{c.mode === 'sequence' ? ' · sequence' : ''}</span>
                </div>
                {c.subject && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>“{c.subject}”</div>}
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{c.sentAt ? `Sent ${when(c.sentAt)}` : c.approvedAt ? `Approved ${when(c.approvedAt)}` : `Created ${when(c.createdAt)}`}{c.audienceCount ? ` · audience ${fmt(c.audienceCount)}` : ''}</div>
                <div style={stats}>
                  <Stat label="Sent" value={fmt(c.results.sent)} />
                  <Stat label="Opens" value={fmt(c.results.opens)} sub={pct(c.results.opens, c.results.sent)} />
                  <Stat label="Clicks" value={fmt(c.results.clicks)} sub={pct(c.results.clicks, c.results.sent)} />
                  {c.results.converted > 0 && <Stat label="Converted" value={c.results.convertedTickets > 0 ? `${fmt(c.results.convertedTickets)} tickets` : fmt(c.results.converted)} sub={c.results.convertedTickets > 0 ? `${fmt(c.results.converted)} buyers` : ''} />}
                  {c.results.convertedValue > 0 && <Stat label="Value" value={fmtR(c.results.convertedValue)} />}
                </div>
              </div>
            ))}
          </section>
        )}
        {data && data.includes.includes('segments') && (
          <section style={{ marginTop: 22 }}>
            <h3 style={h3}>Segments <span style={count}>{data.segments.length}</span></h3>
            {data.segments.length === 0 && <div style={hint}>No segments are linked to this event yet.</div>}
            {data.segments.map((s) => (
              <div key={s.id} style={card}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 14, flex: 1, minWidth: 0 }}>{s.name}</span>
                  <span style={pillMuted}>{fmt(s.count)} people</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{s.lastResolvedAt ? `Last counted ${when(s.lastResolvedAt)}` : 'Not counted yet'}</div>
              </div>
            ))}
          </section>
        )}
        {data && !data.includes.length && <div style={{ ...hint, marginTop: 16 }}>Only the dashboards of this event are shared with you.</div>}
        <p style={{ ...hint, marginTop: 22 }}><Link to="/" style={{ color: 'var(--brand)' }}>← Back to your events</Link></p>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div style={{ minWidth: 72 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>{value}{sub && sub !== '—' ? <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginLeft: 5 }}>{sub}</span> : null}</div>
    </div>
  );
}

const hint = { fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, margin: '6px 0 0' };
const h3 = { fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted-2)', margin: '0 0 8px', display: 'flex', gap: 8, alignItems: 'center' };
const count = { fontSize: 11, fontWeight: 700, borderRadius: 980, padding: '1px 8px', background: 'var(--card)', border: '1px solid var(--hairline)', color: 'var(--muted)' };
const card = { border: '1px solid var(--hairline)', borderRadius: 12, padding: '12px 14px', background: 'var(--card)', marginBottom: 8 };
const stats = { display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10 };
const pill = { fontSize: 10.5, fontWeight: 800, borderRadius: 980, padding: '2px 9px', background: 'rgba(var(--brand-rgb),0.12)', color: 'var(--brand)', textTransform: 'capitalize' };
const pillMuted = { fontSize: 10.5, fontWeight: 700, borderRadius: 980, padding: '2px 9px', background: 'var(--frost)', border: '1px solid var(--hairline)', color: 'var(--muted)' };
